import React, { useEffect, useState } from 'react'
import { Select } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { compose, equals, find, map, prop, propEq, sortBy } from 'ramda'
import { useStore } from '../../../StateProvider'
import { getUserValue } from '../utils'

/**
 * @param {string} countryCode
 * @param {string} countryName
 * @param {string} phoneCode
 * @returns {OptionProps}
 */
const getOption = ({ countryCode, countryName, phoneCode }) => ({
  value: countryCode,
  label: countryName,
  phoneCode
})

/**
 * @param {string} countryCode
 * @returns {Country}
 */
const getCountry = (countryCode) => find(propEq('countryCode', countryCode))
/**
 * @param {Country} country
 * @returns {boolean}
 */
const isFaceMatchRequired = (country) =>
  country ? country.faceMatchRequired : false

/**
 * @param {string} countryCode
 * @param {Country[]} countries
 * @returns {boolean}
 */
const getIsFaceMatchByCountry = (countryCode, countries) =>
  compose(
    isFaceMatchRequired,
    getCountry(countryCode)
  )(countries)

const sortByName = sortBy(prop('label'))

const getCountriesSelectItems = compose(
  sortByName,
  map(getOption)
)

const getDefaultCountry = (countries, defaultCountry) => {
  const defaultCountryObj = countries.find(country => country.countryCode === defaultCountry)
  return defaultCountryObj ? defaultCountry : ''
}

const ExternalSelectCountries = ({ i18nLabel, name, additionalInfo, showLabelAsPlaceholder }) => {
  const { t } = useTranslation()
  const [
    {
      whitelabel: { defaultCountry },
      user,
      data: {
        countries: storeCountries = [],
        cobCountries: storeCobCountries = [],
        citizenshipCountries: storeCitizenshipCountries = []
      },
      form: { accId }
    },
    dispatch
  ] = useStore()
  const [value, setValue] = useState('')
  const [countries, setCountries] = useState([])
  const [countriesRaw, setCountriesRaw] = useState([])
  const [isValid, setIsValid] = useState(null)
  const citizenshipCountry = accId === 0
    ? user.citizenshipCountry
    : user.jointAccounts?.[accId - 1]?.citizenshipCountry
  const isCountryOfBirth = name === 'countryOfBirth'
  const isCitizenshipCountry = name === 'citizenshipCountry'

  useEffect(() => {
    if (isCountryOfBirth) return
    if (isCitizenshipCountry) return
    initCountries()
  }, [storeCountries])

  useEffect(() => {
    if (!isCountryOfBirth) return
    initCountries()
  }, [storeCobCountries])

  useEffect(() => {
    if (!isCitizenshipCountry) return
    initCountries()
  }, [storeCitizenshipCountries])

  useEffect(() => {
    if (isCountryOfBirth) return
    if (isCitizenshipCountry) return
    updateValue()
  }, [storeCountries, defaultCountry, user, name])

  useEffect(() => {
    if (!isCountryOfBirth) return
    updateValue()
  }, [storeCobCountries, defaultCountry, user, name])

  useEffect(() => {
    if (!isCitizenshipCountry) return
    updateValue()
  }, [storeCitizenshipCountries, defaultCountry, user, name])

  useEffect(() => {
    if (citizenshipCountry && name === 'citizenshipCountry' && countriesRaw.length) {
      const isFaceMatch = getIsFaceMatchByCountry(citizenshipCountry, countriesRaw)
      dispatch({ type: 'setExtFieldValue', name: 'isFaceMatch', value: isFaceMatch })
    }
  }, [citizenshipCountry, countriesRaw, name])

  const getCountries = () => {
    switch (name) {
      case 'countryOfBirth':
        return storeCobCountries
      case 'citizenshipCountry':
        return storeCitizenshipCountries
      default:
        return storeCountries
    }
  }

  const initCountries = () => {
    const sC = getCountries()
    setCountries(getCountriesSelectItems(sC))
    setCountriesRaw(sC)
  }

  const updateValue = () => {
    const sC = getCountries()
    if (!sC.length || value.length) return
    const userCountry = getUserValue(accId, user, name)
    const presetCountry = userCountry || getDefaultCountry(sC, defaultCountry)
    if (presetCountry) {
      const foundCountry = sC.find(({ countryCode }) => countryCode === presetCountry)
      if (foundCountry) {
        setValue(presetCountry)
        setIsValid(!!presetCountry.length)
        dispatchValue(presetCountry)
      } else {
        console.log('country not found', name, presetCountry)
      }
    }
  }

  /**
   * @param {string} value
   */
  const getSelectValue = (value) =>
    equals(value, t('generatedField.choose')) ||
    equals(value, t('generatedField.citizenshipPlaceholder'))
      ? ''
      : value

  const onChange = ({ target: { value } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    setIsValid(!!selectedValue.length)
    dispatchValue(selectedValue)
    if (name === 'citizenshipCountry') {
      if (value === '') {
        dispatch({ type: 'setExtFieldValue', name: 'isFaceMatch', value: false })
      } else {
        const isFaceMatch = getIsFaceMatchByCountry(selectedValue, countriesRaw)
        dispatch({ type: 'setExtFieldValue', name: 'isFaceMatch', value: isFaceMatch })
      }
    }
  }

  const dispatchValue = value => {
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: value
    })

    dispatch({
      type: 'setValidationFld',
      name,
      value: !!value.length
    })
  }

  return (
    <Select
      value={value}
      label={i18nLabel ? t(`generatedField.${i18nLabel}`) : ''}
      placeholder={t('generatedField.choose')}
      options={countries}
      onChange={onChange}
      name={name}
      key={name}
      error=''
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
      showLabelAsPlaceholder={showLabelAsPlaceholder}
    />
  )
}

const getExternalCountryFields = showLabelAsPlaceholder =>
  map(countryField => {
    const { i18n, name, additionalInfo, order } = countryField
    return (
      <ExternalSelectCountries
        i18nLabel={i18n}
        name={name}
        key={name}
        additionalInfo={additionalInfo}
        showLabelAsPlaceholder={showLabelAsPlaceholder}
        order={order}
      />
    )
  })

export { ExternalSelectCountries, getExternalCountryFields, getCountriesSelectItems }
